import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import {
  UserMinus, AlertTriangle, Upload, Download, Check, X,
  Calendar, FileText, RotateCcw, Trash2,
} from "lucide-react";
import { formatDate } from "@/lib/dates";
import { useAuth } from "@/lib/auth";

const REASONS = [
  { v: "resigned",         label: "Resigned" },
  { v: "terminated",       label: "Terminated" },
  { v: "redundancy",       label: "Redundancy" },
  { v: "end_of_contract",  label: "End of contract" },
  { v: "retired",          label: "Retired" },
  { v: "other",            label: "Other" },
];

export default function EmployeeLeaverTab({ emp, onSaved }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [leaver, setLeaver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showReverse, setShowReverse] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leavers/employee/${emp.id}`);
      setLeaver(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leaver record");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [emp.id]);

  if (loading) return <div className="text-center py-8 text-[var(--cc-text-soft)]">Loading leaver record…</div>;

  // No leaver — show CTA
  if (!leaver) {
    return (
      <div className="cc-card p-8" data-testid="leaver-tab-empty">
        <div className="flex items-center gap-3 mb-3">
          <UserMinus className="w-6 h-6 text-[var(--cc-accent)]" />
          <h3 className="font-display text-xl font-bold">Not a leaver</h3>
        </div>
        <p className="text-sm text-[var(--cc-text-soft)] mb-4 max-w-2xl">
          {emp.full_name} is currently an active employee. Use <strong>Mark as leaver</strong>
          {" "}to start the offboarding process — capture their leaving reason, notice period,
          resignation/termination letter, rehire eligibility and exit-interview notes.
          Confirming the leaver deactivates their login and closes out pending policies.
        </p>
        <button
          className="btn-primary inline-flex items-center gap-1.5"
          onClick={() => setShowCreate(true)}
          data-testid="leaver-start-button"
        >
          <UserMinus className="w-4 h-4" /> Mark as leaver
        </button>

        {showCreate && (
          <LeaverFormModal
            emp={emp}
            initial={null}
            onClose={() => setShowCreate(false)}
            onSaved={() => { setShowCreate(false); load(); onSaved?.(); }}
          />
        )}
      </div>
    );
  }

  const isDraft = leaver.status === "draft";
  const isConfirmed = leaver.status === "confirmed";

  return (
    <div className="space-y-4" data-testid="leaver-tab">
      {/* Status banner */}
      <div className={`cc-card p-4 border-l-4 ${isConfirmed ? "border-red-500 bg-red-50/50" : "border-amber-500 bg-amber-50/40"}`} data-testid="leaver-status-banner">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Leaver record
            </div>
            <div className="font-display text-2xl font-bold inline-flex items-center gap-2">
              {isConfirmed
                ? <span className="text-red-700">Confirmed leaver</span>
                : <span className="text-amber-700">Draft — not yet confirmed</span>}
              <span className="pill !text-[10px] pill-info">{leaver.reason}</span>
            </div>
            <div className="text-xs text-[var(--cc-text-soft)] mt-1">
              Created {formatDate(leaver.created_at)} by {leaver.created_by_name || "—"}
              {isConfirmed && ` · Confirmed ${formatDate(leaver.confirmed_at)} by ${leaver.confirmed_by_name || "—"}`}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isDraft && (
              <>
                <button className="btn-ghost text-xs" onClick={() => setEditing(true)} data-testid="leaver-edit-button">
                  Edit details
                </button>
                <button
                  className="btn-ghost text-xs text-[var(--cc-accent)] inline-flex items-center gap-1"
                  onClick={async () => {
                    if (!window.confirm("Discard this draft leaver record? This cannot be undone.")) return;
                    try {
                      await api.delete(`/leavers/${leaver.id}`);
                      toast.success("Draft discarded");
                      load();
                      onSaved?.();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed to discard"); }
                  }}
                  data-testid="leaver-discard-button"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Discard
                </button>
                <button className="btn-primary text-xs inline-flex items-center gap-1.5" onClick={() => setShowConfirm(true)} data-testid="leaver-confirm-button">
                  <Check className="w-3.5 h-3.5" /> Confirm leaver
                </button>
              </>
            )}
            {isConfirmed && isSuper && (
              <button className="btn-ghost text-xs text-[var(--cc-accent)] inline-flex items-center gap-1" onClick={() => setShowReverse(true)} data-testid="leaver-reverse-button">
                <RotateCcw className="w-3.5 h-3.5" /> Reverse
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Key facts */}
      <div className="cc-card p-6">
        <h3 className="font-display text-lg font-bold mb-4">Leaver details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <KV label="Reason" value={REASONS.find((r) => r.v === leaver.reason)?.label || leaver.reason} />
          <KV label="Resignation date" value={formatDate(leaver.resignation_date)} icon={Calendar} />
          <KV label="Last working day" value={formatDate(leaver.last_working_day)} icon={Calendar} accent />
          <KV label="Notice (days)" value={leaver.notice_days != null ? String(leaver.notice_days) : "—"} />
          <KV label="Rehire eligible" value={
            leaver.rehire_eligible === true ? "Yes"
              : leaver.rehire_eligible === false ? "No"
              : "Not decided"
          } />
          <KV label="Status" value={leaver.status} />
        </div>
        {leaver.reason_notes && (
          <div className="mt-4">
            <div className="label-eyebrow mb-1">Reason notes</div>
            <div className="text-sm bg-[var(--cc-surface)] rounded-md p-3 whitespace-pre-wrap">{leaver.reason_notes}</div>
          </div>
        )}
        {leaver.rehire_notes && (
          <div className="mt-4">
            <div className="label-eyebrow mb-1">Rehire notes</div>
            <div className="text-sm bg-[var(--cc-surface)] rounded-md p-3 whitespace-pre-wrap">{leaver.rehire_notes}</div>
          </div>
        )}
        {leaver.exit_interview_notes && (
          <div className="mt-4">
            <div className="label-eyebrow mb-1">Exit interview notes</div>
            <div className="text-sm bg-[var(--cc-surface)] rounded-md p-3 whitespace-pre-wrap">{leaver.exit_interview_notes}</div>
          </div>
        )}
      </div>

      {/* Letter section */}
      <LetterCard leaver={leaver} onChanged={load} />

      {/* Actions taken (post-confirm) */}
      {isConfirmed && leaver.actions_taken?.length > 0 && (
        <div className="cc-card p-6" data-testid="leaver-actions-taken">
          <h3 className="font-display text-lg font-bold mb-3">Actions taken on confirmation</h3>
          <ul className="text-sm space-y-1">
            {leaver.actions_taken.map((a) => (
              <li key={a} className="inline-flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-[var(--cc-sage)]" /> {a.replace(/_/g, " ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modals */}
      {editing && (
        <LeaverFormModal
          emp={emp}
          initial={leaver}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
      {showConfirm && (
        <ConfirmLeaverModal
          leaver={leaver}
          emp={emp}
          onClose={() => setShowConfirm(false)}
          onSaved={() => { setShowConfirm(false); load(); onSaved?.(); }}
        />
      )}
      {showReverse && (
        <ReverseLeaverModal
          leaver={leaver}
          onClose={() => setShowReverse(false)}
          onSaved={() => { setShowReverse(false); load(); onSaved?.(); }}
        />
      )}
    </div>
  );
}

function KV({ label, value, icon: Icon, accent }) {
  return (
    <div>
      <div className="label-eyebrow mb-1 inline-flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</div>
      <div className={`text-sm font-medium ${accent ? "text-[var(--cc-accent)]" : ""}`}>{value || <span className="italic text-[var(--cc-text-soft)]">Not set</span>}</div>
    </div>
  );
}

function LetterCard({ leaver, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/leavers/${leaver.id}/document`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Letter uploaded");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const { data } = await api.get(`/leavers/${leaver.id}/document`);
      window.open(data.url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="cc-card p-6" data-testid="leaver-letter-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-lg font-bold inline-flex items-center gap-2">
          <FileText className="w-5 h-5" /> Resignation / Termination letter
        </h3>
        <div className="flex gap-2">
          {leaver.letter_filename && (
            <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={download} data-testid="leaver-download-letter">
              <Download className="w-3.5 h-3.5" /> Download
            </button>
          )}
          {leaver.status === "draft" && (
            <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1" data-testid="leaver-upload-letter">
              <Upload className="w-3.5 h-3.5" /> {leaver.letter_filename ? "Replace" : "Upload"}
              <input type="file" hidden accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
            </label>
          )}
        </div>
      </div>
      <div className="text-sm text-[var(--cc-text-soft)] mt-2">
        {leaver.letter_filename ? (
          <>Uploaded: <strong>{leaver.letter_filename}</strong> · {formatDate(leaver.letter_uploaded_at)}</>
        ) : "No letter uploaded yet."}
      </div>
    </div>
  );
}

// ============================================================================
//  Modals
// ============================================================================
function LeaverFormModal({ emp, initial, onClose, onSaved }) {
  const [reason, setReason] = useState(initial?.reason || "resigned");
  const [reasonNotes, setReasonNotes] = useState(initial?.reason_notes || "");
  const [resignationDate, setResignationDate] = useState(initial?.resignation_date || "");
  const [lastWorkingDay, setLastWorkingDay] = useState(initial?.last_working_day || "");
  const [noticeDays, setNoticeDays] = useState(initial?.notice_days ?? "");
  const [rehireEligible, setRehireEligible] = useState(
    initial?.rehire_eligible === true ? "yes"
    : initial?.rehire_eligible === false ? "no"
    : "",
  );
  const [rehireNotes, setRehireNotes] = useState(initial?.rehire_notes || "");
  const [exitNotes, setExitNotes] = useState(initial?.exit_interview_notes || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) return toast.error("Select a reason");
    setBusy(true);
    const payload = {
      reason,
      reason_notes: reasonNotes || null,
      resignation_date: resignationDate || null,
      last_working_day: lastWorkingDay || null,
      notice_days: noticeDays === "" ? null : Number(noticeDays),
      rehire_eligible: rehireEligible === "yes" ? true : rehireEligible === "no" ? false : null,
      rehire_notes: rehireNotes || null,
      exit_interview_notes: exitNotes || null,
    };
    try {
      if (initial) {
        await api.patch(`/leavers/${initial.id}`, payload);
        toast.success("Leaver record updated");
      } else {
        await api.post("/leavers", { employee_id: emp.id, ...payload });
        toast.success("Leaver record created");
      }
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-2xl" testId="leaver-form-modal">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="font-display text-xl font-bold">{initial ? "Edit leaver record" : `Mark ${emp.full_name} as leaver`}</h2>
          <p className="text-xs text-[var(--cc-text-soft)] mt-1">Draft — nothing is deactivated until you Confirm.</p>
        </div>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Reason *</label>
          <select className="cc-select" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="leaver-reason">
            {REASONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Rehire eligible?</label>
          <select className="cc-select" value={rehireEligible} onChange={(e) => setRehireEligible(e.target.value)} data-testid="leaver-rehire">
            <option value="">Not decided</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Resignation / notice date</label>
          <input type="date" className="cc-input" value={resignationDate} onChange={(e) => setResignationDate(e.target.value)} data-testid="leaver-resignation-date" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Last working day *</label>
          <input type="date" className="cc-input" value={lastWorkingDay} onChange={(e) => setLastWorkingDay(e.target.value)} data-testid="leaver-last-day" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Notice (days)</label>
          <input type="number" min="0" max="365" className="cc-input" value={noticeDays} onChange={(e) => setNoticeDays(e.target.value)} data-testid="leaver-notice-days" />
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-1">Reason notes</label>
          <textarea className="cc-input" rows={2} value={reasonNotes} onChange={(e) => setReasonNotes(e.target.value)} data-testid="leaver-reason-notes" placeholder="Extra context (e.g. 'Moving abroad', 'Performance — see disciplinary log')" />
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-1">Rehire notes</label>
          <textarea className="cc-input" rows={2} value={rehireNotes} onChange={(e) => setRehireNotes(e.target.value)} data-testid="leaver-rehire-notes" />
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow block mb-1">Exit interview notes</label>
          <textarea className="cc-input" rows={3} value={exitNotes} onChange={(e) => setExitNotes(e.target.value)} data-testid="leaver-exit-notes" placeholder="Key takeaways from the exit interview…" />
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="leaver-save-button">
          {busy ? "Saving…" : (initial ? "Save changes" : "Create leaver record")}
        </button>
      </div>
    </Modal>
  );
}

function ConfirmLeaverModal({ leaver, emp, onClose, onSaved }) {
  const [deactivate, setDeactivate] = useState(true);
  const [revokePolicies, setRevokePolicies] = useState(true);
  const [closeContracts, setCloseContracts] = useState(true);
  const [notifyCC, setNotifyCC] = useState(true);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!ack) return toast.error("Please acknowledge to proceed");
    setBusy(true);
    try {
      const { data } = await api.post(`/leavers/${leaver.id}/confirm`, {
        deactivate_login: deactivate,
        revoke_pending_policies: revokePolicies,
        close_contracts: closeContracts,
        notify_care_connect: notifyCC,
      });
      toast.success(`Leaver confirmed — ${data.actions?.length || 0} actions taken`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Confirm failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-lg" testId="leaver-confirm-modal">
      <div className="flex justify-between items-start mb-3">
        <h2 className="font-display text-xl font-bold text-red-700 inline-flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" /> Confirm leaver
        </h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <p className="text-sm mb-4">
        You are about to finalise <strong>{emp.full_name}</strong> as a leaver
        (last working day: <strong>{formatDate(leaver.last_working_day)}</strong>).
        This will trigger the following actions:
      </p>
      <div className="space-y-2 mb-4">
        <Check2 v={deactivate} onChange={setDeactivate} testId="conf-deactivate"
          label="Deactivate their login account" />
        <Check2 v={revokePolicies} onChange={setRevokePolicies} testId="conf-revoke"
          label="Cancel all pending policy acknowledgments" />
        <Check2 v={closeContracts} onChange={setCloseContracts} testId="conf-contracts"
          label="Mark employment as 'leaver' + set last working day on profile" />
        <Check2 v={notifyCC} onChange={setNotifyCC} testId="conf-notify"
          label="Send staff.leaver webhook to Care Connect" />
      </div>
      <label className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-3 cursor-pointer" data-testid="conf-ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
        <span>
          I understand this cannot be undone from the HR Admin console (only Super Admin can reverse a confirmed leaver).
        </span>
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary bg-red-600 hover:bg-red-700 border-red-600" onClick={submit} disabled={busy || !ack} data-testid="leaver-confirm-submit">
          {busy ? "Confirming…" : "Confirm leaver"}
        </button>
      </div>
    </Modal>
  );
}

function Check2({ v, onChange, label, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={testId}>
      <input type="checkbox" checked={v} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function ReverseLeaverModal({ leaver, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 4) return toast.error("Please supply a reason (4+ chars)");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("reason", reason.trim());
      await api.post(`/leavers/${leaver.id}/reverse`, form);
      toast.success("Leaver reversed — user reactivated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} widthClass="max-w-md" testId="leaver-reverse-modal">
      <div className="flex justify-between items-start mb-3">
        <h2 className="font-display text-xl font-bold">Reverse leaver</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <p className="text-sm text-[var(--cc-text-soft)] mb-3">
        This will re-activate the user, restore employment status to active and mark the leaver record as reversed. Audit-logged.
      </p>
      <label className="label-eyebrow block mb-1">Reason (required)</label>
      <textarea className="cc-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 400))} data-testid="leaver-reverse-reason" placeholder="e.g. 'Withdrawal of resignation accepted by NI'" />
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy || reason.trim().length < 4} data-testid="leaver-reverse-submit">
          {busy ? "Reversing…" : "Reverse leaver"}
        </button>
      </div>
    </Modal>
  );
}
